'use client';

import { useCallback } from 'react';
import { gatewayService } from '@/lib/api/services/gateway';
import { useScopeStore } from '@/lib/stores/scope-store';
import type { Workspace } from '@/components/shell/simple/constants';

/**
 * Returns a function that mints a project-scoped gateway API key for a given
 * Simple Mode workspace. The workspace `slug` is the project id; org/dept come
 * from the active scope. Throws (surfaced as a toast) when scope is missing or
 * the gateway rejects the request.
 */
export function useGenerateApiKey(): (workspace: Workspace) => Promise<string> {
  const orgId = useScopeStore((s) => s.org?.id);
  const deptId = useScopeStore((s) => s.department?.id);

  return useCallback(
    async (workspace: Workspace) => {
      if (!orgId) throw new Error('No organization in scope — sign in first.');
      if (!workspace?.slug) throw new Error('Select a workspace first.');
      const created = await gatewayService.createApiKey({
        orgId,
        deptId,
        projectId: workspace.slug,
        name: `Simple Mode — ${workspace.name || workspace.slug}`,
      });
      return created.secret;
    },
    [orgId, deptId],
  );
}
